import { defineStore, acceptHMRUpdate } from "pinia";
import Api from "src/utils/api";
import { useProfileStore } from "./profileStore";
import Helper from "../utils/helper";
import Constants from "@/utils/constants";

export const usePermissionsStore = defineStore("permissions_store", {
  persist: true,
  state: () => ({
    permissions: {}
  }),
  getters: {
    /**
     * Gets the current permissions state
     * @returns {Object} The permissions object containing all permission levels and their associated data
     */
    getPermissions: (state) => state.permissions
  },
  actions: {
    /**
     * Fetches and sets permissions from the profile store
     * @returns {Promise<Object>} The permissions response from the API
     */
    async setPermissions() {
      const callback = (permissions) => {
        if (Helper.isValueExist(permissions)) {
          this.permissions = permissions;
        }
      };
      Api.Permissions.subscribeToPermissions(callback);
      const profileStore = useProfileStore();
      const response = await profileStore.getPermissions();
      this.permissions = response;
      return response;
    },

    /**
     * Gets combined permissions for a specific permission level and all higher levels
     * @param {string} permissionsLevel - The permission level to start from (READER, WRITER, APPROVER, ADMIN)
     * @param {Object} [permissions=this.permissions] - The permissions object to use (defaults to store permissions)
     * @returns {Promise<Object>} Combined permissions object with arrays for form, shop, region, company, and dashboard permissions
     */
    getCombinedPermssions(permissionsLevel, permissions = this.permissions) {
      // Define permission hierarchy in ascending order
      const permissionHierarchy = [
        Constants.enums.PERMISSION_LEVELS.READER.key,
        Constants.enums.PERMISSION_LEVELS.WRITER.key,
        Constants.enums.PERMISSION_LEVELS.APPROVER.key,
        Constants.enums.PERMISSION_LEVELS.ADMIN.key
      ];

      // Find the index of the requested permission level
      const requestedLevelIndex = permissionHierarchy.indexOf(permissionsLevel);

      if (requestedLevelIndex === -1) {
        // If permission level not found, return empty object
        return {};
      }

      // Get all permission levels from requested level and above
      const allowedLevels = permissionHierarchy.slice(requestedLevelIndex);

      // Initialize combined permissions object
      const combinedPermissions = {
        form: [],
        shop: [],
        dashboard: []
      };

      // Combine permissions from allowed levels
      allowedLevels.forEach((level) => {
        if (permissions[level]) {
          // Combine each permission type
          Object.keys(combinedPermissions).forEach((permissionType) => {
            if (
              permissions[level][permissionType] &&
              Array.isArray(permissions[level][permissionType])
            ) {
              combinedPermissions[permissionType].push(
                ...permissions[level][permissionType]
              );
            }
          });
        }
      });

      // Remove duplicates from each permission type
      Object.keys(combinedPermissions).forEach((permissionType) => {
        combinedPermissions[permissionType] = [
          ...new Set(combinedPermissions[permissionType])
        ];
      });

      combinedPermissions.region = [...(permissions.region || [])];
      combinedPermissions.company = [...(permissions.company || [])];

      return combinedPermissions;
    },
    /**
     * Fetches and filters companies based on user permissions
     * @param {Object} queryParams - Query parameters for the API request
     * @param {string} permissionsLevel - The permission level to check against
     * @param {Object} [permissions=this.permissions] - The permissions object to use (defaults to store permissions)
     * @returns {Promise<Object|Array>} API response with filtered companies or empty array on error
     */
    async getCompanies(
      queryParams,
      permissionsLevel,
      permissions = this.permissions
    ) {
      try {
        // Fetch all companies from API
        const response = await Api.Permissions.getMasterCompanies(queryParams);

        if (!response || !response.data || !Array.isArray(response.data)) {
          return [];
        }

        // Get combined permissions for the specified level
        const combinedPermissions = await this.getCombinedPermssions(
          permissionsLevel,
          permissions
        );

        if (
          combinedPermissions?.company?.includes(
            Constants.enums.SUPER_ADMIN_ID.SUPER_ADMIN_ID
          ) ||
          permissionsLevel === Constants.enums.PERMISSION_LEVELS.NONE.key
        ) {
          return response;
        }

        // Filter companies based on permissions (only by company ID)
        const filteredCompanies = response.data.filter((company) => {
          // Check if user has permission for this company's ID
          const hasCompanyPermission = combinedPermissions?.company?.includes(
            company.id
          );

          return hasCompanyPermission;
        });

        response.data = filteredCompanies;

        return response;
      } catch (error) {
        console.error("Error fetching companies:", error);
        return [];
      }
    },
    /**
     * Fetches and filters regions based on user permissions
     * @param {Object} queryParams - Query parameters for the API request
     * @param {string} permissionsLevel - The permission level to check against
     * @param {Object} [permissions=this.permissions] - The permissions object to use (defaults to store permissions)
     * @returns {Promise<Object|Array>} API response with filtered regions or empty array on error
     */
    async getRegions(
      queryParams,
      permissionsLevel,
      permissions = this.permissions
    ) {
      try {
        // Fetch all regions from API
        const response = await Api.Permissions.getMasterRegions(queryParams);

        if (!response || !response.data || !Array.isArray(response.data)) {
          return [];
        }

        // Get combined permissions for the specified level
        const combinedPermissions = await this.getCombinedPermssions(
          permissionsLevel,
          permissions
        );

        if (
          combinedPermissions?.region?.includes(
            Constants.enums.SUPER_ADMIN_ID.SUPER_ADMIN_ID
          ) ||
          permissionsLevel === Constants.enums.PERMISSION_LEVELS.NONE.key
        ) {
          return response;
        }

        // Filter regions based on permissions
        const filteredRegions = response.data.filter((region) => {
          // Check if user has permission for this region's ID
          const hasRegionPermission = combinedPermissions?.region?.includes(
            region.id
          );

          // Return true if user has any of the required permissions
          return hasRegionPermission;
        });

        response.data = filteredRegions;

        return response;
      } catch (error) {
        console.error("Error fetching regions:", error);
        return [];
      }
    },
    /**
     * Fetches and filters shops based on user permissions
     * @param {Object} queryParams - Query parameters for the API request
     * @param {string} permissionsLevel - The permission level to check against
     * @param {Object} [permissions=this.permissions] - The permissions object to use (defaults to store permissions)
     * @returns {Promise<Object|Array>} API response with filtered shops or empty array on error
     */
    async getShops(
      queryParams,
      permissionsLevel,
      permissions = this.permissions
    ) {
      try {
        // Fetch all shops from API
        const response = await Api.Permissions.getMasterShops(queryParams);

        if (!response || !response.data || !Array.isArray(response.data)) {
          return [];
        }

        // Get combined permissions for the specified level
        const combinedPermissions = await this.getCombinedPermssions(
          permissionsLevel,
          permissions
        );

        if (
          combinedPermissions?.shop?.includes(
            Constants.enums.SUPER_ADMIN_ID.SUPER_ADMIN_ID
          ) ||
          permissionsLevel === Constants.enums.PERMISSION_LEVELS.NONE.key
        ) {
          console.log("super admin");
          return response;
        }

        // Filter shops based on permissions
        const filteredShops = response.data.filter((shop) => {
          // Check if user has permission for this specific shop
          const hasShopPermission = combinedPermissions?.shop?.includes(
            shop.id
          );

          // Return true if user has any of the required permissions
          return hasShopPermission;
        });

        response.data = filteredShops;

        return response;
      } catch (error) {
        console.error("Error fetching shops:", error);
        return [];
      }
    }
  },
  $reset() {}
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePermissionsStore, import.meta.hot));
}
